import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ShieldCheck,
  FileText,
  BookOpen,
  Users,
} from 'lucide-react';
import HussarTopNav from '../components/HussarTopNav';

/**
 * WiedzaIndex
 *
 * Static knowledge index page. Lists the two existing HussarAi
 * educational articles and strengthens internal linking around the
 * "/wiedza/" content section.
 *
 * Strict scope:
 *  - Static page. No state. No forms. No uploads. No API. No auth.
 *  - Backend untouched.
 *  - Tone: calm · educational · people-first · institution-friendly.
 *  - No legal advice. No status determination. No certification claim.
 *  - No LW references.
 */
const META_TITLE = 'Wiedza o aktach imigracyjnych i AI | HussarAi';
const META_DESCRIPTION =
  'Przewodniki HussarAi o aktach imigracyjnych, dokumentach, AI, ' +
  'śladzie audytowym i weryfikacji przez człowieka.';
const CANONICAL_URL = 'https://hussarai.org/wiedza';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
    if (selector.includes('property=')) {
      const prop = selector.match(/property="([^"]+)"/)?.[1];
      if (prop) el.setAttribute('property', prop);
    } else if (selector.includes('name=')) {
      const name = selector.match(/name="([^"]+)"/)?.[1];
      if (name) el.setAttribute('name', name);
    } else if (selector.includes('rel=')) {
      const rel = selector.match(/rel="([^"]+)"/)?.[1];
      if (rel) el.setAttribute('rel', rel);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const ARTICLES = [
  {
    id: 'dlaczego-zbudowalem-hussarai',
    route: '/wiedza/dlaczego-zbudowalem-hussarai',
    published: '2026-02-19',
    title: 'Dlaczego zbudowałem HussarAi: AI przygotowuje, człowiek decyduje',
    lede:
      'Manifest założycielski HussarAi — dlaczego akta imigracyjne ' +
      'potrzebują integralności, dlaczego AI powinno przygotowywać ' +
      'informacje, ale nigdy nie decydować o statusie, i dlaczego nadzór ' +
      'człowieka jest kluczowy w europejskich ramach nadzoru nad AI.',
  },
  {
    id: 'w-europie-ai-musi-byc-przejrzyste',
    route: '/wiedza/w-europie-ai-musi-byc-przejrzyste',
    published: '2026-08-17',
    title: 'W Europie AI musi być przejrzyste',
    lede:
      'Europejskie ramy nadzoru nad AI wymagają widocznego oddzielenia ' +
      'faktu źródłowego, obserwacji AI i weryfikacji człowieka. Jak wpisuje ' +
      'się w to architektura HussarAi.',
  },
  {
    id: 'dlaczego-hussarai-nie-pozwala-ai-decydowac',
    route: '/wiedza/dlaczego-hussarai-nie-pozwala-ai-decydowac',
    published: '2026-08-17',
    title: 'Dlaczego HussarAi nie pozwala AI podejmować decyzji imigracyjnych',
    lede:
      'Cztery warstwy: fakty źródłowe, sygnały weryfikacyjne, weryfikacja ' +
      'przez człowieka, decyzja właściwego urzędu. HussarAi kończy na ' +
      'warstwie 3.',
  },
  {
    id: 'kazda-obserwacja-ai-slad-zrodlowy',
    route: '/wiedza/kazda-obserwacja-ai-slad-zrodlowy',
    published: '2026-08-17',
    title: 'Każda obserwacja AI powinna mieć swój ślad źródłowy',
    lede:
      'Dlaczego każdą obserwację AI w aktach imigracyjnych warto ' +
      'sprowadzić do prostego łańcucha: dokument źródłowy → fakt → ' +
      'obserwacja AI → weryfikacja człowieka → uporządkowany rekord.',
  },
  {
    id: 'ai-w-aktach-imigracyjnych',
    route: '/wiedza/ai-w-aktach-imigracyjnych',
    title:
      'AI w aktach imigracyjnych: jak uporządkować dokumenty bez ' +
      'oddawania decyzji maszynie',
    lede:
      'Jak AI może pomagać w porządkowaniu dokumentów, terminów ' +
      'i śladu audytowego bez ustalania statusu imigracyjnego.',
  },
  {
    id: 'jak-uporzadkowac-akta-imigracyjne',
    route: '/wiedza/jak-uporzadkowac-akta-imigracyjne',
    title:
      'Jak uporządkować akta imigracyjne przed rozmową ' +
      'z prawnikiem, NGO lub instytucją',
    lede:
      'Praktyczny przewodnik po dokumentach, terminach, pytaniach ' +
      'do recenzenta i bezpiecznym rozpoczęciu szkicu akt.',
  },
];

const WiedzaIndex = () => {
  useEffect(() => {
    document.title = META_TITLE;
    setMeta('meta[name="description"]', 'content', META_DESCRIPTION);
    setMeta('meta[property="og:title"]', 'content', META_TITLE);
    setMeta('meta[property="og:description"]', 'content', META_DESCRIPTION);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:url"]', 'content', CANONICAL_URL);
    setMeta('link[rel="canonical"]', 'href', CANONICAL_URL);
  }, []);

  return (
    <main
      data-testid="wiedza-index-page"
      style={{
        minHeight: '100vh',
        background: 'var(--hussar-bg-page, #0B1220)',
        color: 'var(--hussar-text-primary, #E5E7EB)',
        padding: '56px 20px 96px',
      }}
    >
      <HussarTopNav />
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <Link
          to="/"
          data-testid="wiedza-index-back-link"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: 'rgba(125,211,252,0.85)',
            textDecoration: 'none', marginBottom: 28,
          }}
        >
          <ArrowLeft className="w-4 h-4" /> Powrót do HussarAi
        </Link>

        <p
          data-testid="wiedza-index-eyebrow"
          style={{
            fontSize: 11, letterSpacing: 1.6, fontWeight: 700,
            color: '#7DD3FC', textTransform: 'uppercase', marginBottom: 14,
          }}
        >
          WIEDZA · AKTA IMIGRACYJNE · POD KONTROLĄ CZŁOWIEKA
        </p>

        <h1
          data-testid="wiedza-index-h1"
          style={{
            fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800,
            lineHeight: 1.2, marginBottom: 18, color: '#F1F5F9',
          }}
        >
          Wiedza HussarAi: akta imigracyjne, AI i&nbsp;weryfikacja przez
          człowieka
        </h1>

        <p
          data-testid="wiedza-index-intro"
          style={{
            fontSize: 17, lineHeight: 1.65, color: 'rgba(226,232,240,0.9)',
            marginBottom: 36,
          }}
        >
          Twoja historia nie powinna zniknąć w&nbsp;papierach. W&nbsp;tej
          sekcji wyjaśniamy, jak porządkować akta imigracyjne, jak AI może
          pomagać bez podejmowania decyzji oraz dlaczego weryfikacja przez
          człowieka pozostaje nadrzędna.
        </p>

        <section
          data-testid="wiedza-index-articles"
          aria-label="Artykuły"
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: '1fr',
            marginBottom: 40,
          }}
        >
          {ARTICLES.map((a) => (
            <Link
              key={a.id}
              to={a.route}
              data-testid={`wiedza-index-card-${a.id}`}
              style={{
                display: 'block',
                padding: '22px 24px',
                borderRadius: 14,
                background: 'rgba(15,23,42,0.55)',
                border: '1px solid rgba(125,211,252,0.18)',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'border-color 160ms ease, transform 160ms ease',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#7DD3FC', fontSize: 12, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
                <BookOpen className="w-4 h-4" aria-hidden="true" /> Artykuł
                {a.published && (
                  <span
                    data-testid={`wiedza-index-card-date-${a.id}`}
                    style={{ marginLeft: 8, color: '#94A3B8', fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}
                  >
                    · {a.published}
                  </span>
                )}
              </div>
              <h2
                data-testid={`wiedza-index-card-title-${a.id}`}
                style={{
                  fontSize: 20, fontWeight: 700, lineHeight: 1.3,
                  color: '#F1F5F9', marginBottom: 8,
                }}
              >
                {a.title}
              </h2>
              <p
                data-testid={`wiedza-index-card-lede-${a.id}`}
                style={{
                  fontSize: 15, lineHeight: 1.6,
                  color: 'rgba(226,232,240,0.82)', margin: 0,
                }}
              >
                {a.lede}
              </p>
              <p
                style={{
                  marginTop: 12, marginBottom: 0,
                  color: '#7DD3FC', fontWeight: 600, fontSize: 14,
                }}
              >
                Czytaj →
              </p>
            </Link>
          ))}
        </section>

        <section
          data-testid="wiedza-index-cta-row"
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 12,
            marginBottom: 32,
          }}
        >
          <Link
            to="/moje-akta-imigracyjne"
            data-testid="wiedza-index-primary-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 8, fontWeight: 700,
              background: '#7DD3FC', color: '#0B1220',
              textDecoration: 'none', fontSize: 15,
            }}
          >
            <ShieldCheck className="w-4 h-4" aria-hidden="true" />
            Zacznij od&nbsp;swoich akt
          </Link>
          <Link
            to="/operator-zgodnosci"
            data-testid="wiedza-index-secondary-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 8, fontWeight: 600,
              background: 'rgba(15,23,42,0.55)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(226,232,240,0.92)',
              textDecoration: 'none', fontSize: 14,
            }}
          >
            <Users className="w-4 h-4" aria-hidden="true" />
            Zobacz, jak instytucje weryfikują akta
          </Link>
          <Link
            to="/dashboard-akt-imigracyjnych"
            data-testid="wiedza-index-tertiary-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 8, fontWeight: 600,
              background: 'rgba(15,23,42,0.55)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(226,232,240,0.92)',
              textDecoration: 'none', fontSize: 14,
            }}
          >
            <FileText className="w-4 h-4" aria-hidden="true" />
            Hub Akt Imigracyjnych
          </Link>
        </section>

        <div
          data-testid="wiedza-index-safety-card"
          style={{
            padding: '18px 20px', marginTop: 8,
            background: 'rgba(248,113,113,0.06)',
            border: '1px solid rgba(248,113,113,0.30)',
            borderLeft: '3px solid #F87171',
            borderRadius: 12,
          }}
        >
          <p style={{ fontSize: 12, letterSpacing: 1.2, fontWeight: 700, color: '#F87171', margin: 0, textTransform: 'uppercase' }}>
            Granica systemu
          </p>
          <p
            data-testid="wiedza-index-safety-body"
            style={{ fontSize: 14, lineHeight: 1.6, marginTop: 8, color: 'rgba(226,232,240,0.92)' }}
          >
            HussarAi nie udziela porad prawnych, nie ustala statusu
            imigracyjnego i&nbsp;przygotowuje akta wyłącznie do&nbsp;weryfikacji
            przez człowieka.
          </p>
        </div>

        <p
          data-testid="wiedza-index-doctrine-footer"
          style={{
            marginTop: 36, fontSize: 12, lineHeight: 1.6,
            color: 'rgba(226,232,240,0.55)', fontStyle: 'italic',
          }}
        >
          AI przygotowuje. Władza ludzka decyduje. Każde działanie zostawia
          ślad audytowy.
        </p>
      </div>
    </main>
  );
};

export default WiedzaIndex;
